import React from "react";
import Link from "next/link";
import Icon from "../components/Icon";
import Button from "../components/Button";
import renderIcon from "../../../components/renderIcon";
import { applyLang } from "../../../i18n";
import { resolveImage, resolveUrl, readArrayFromConfig } from "../utils";
import { isExternalUrl, buildLinkHref } from "../../../components/normalizeMenuUrl";

const styles = `
.t26-banners-wrap {
  padding: clamp(12px, 2vw, 24px) clamp(16px, 4vw, 32px);
  max-width: var(--container);
  margin-inline: auto;
  display: flex; flex-direction: column;
  gap: clamp(16px, 2.5vw, 28px);
}
.t26-banner {
  position: relative;
  border-radius: clamp(18px, 2vw, 22px);
  overflow: hidden;
  isolation: isolate;
  min-height: 260px;
  display: flex; align-items: flex-end;
  box-shadow: 0 8px 32px rgba(0,0,0,.12), 0 2px 8px rgba(0,0,0,.06);
}
.t26-banner__img {
  position: absolute; inset: 0;
  width: 100%; height: 100%;
  object-fit: cover; z-index: 0;
}
.t26-banner__wash {
  position: absolute; inset: 0; z-index: 1;
  background: linear-gradient(92deg, rgba(9,18,26,.78) 0%, rgba(9,18,26,.45) 55%, rgba(9,18,26,.15) 100%);
}
[dir="rtl"] .t26-banner__wash {
  background: linear-gradient(-92deg, rgba(9,18,26,.78) 0%, rgba(9,18,26,.45) 55%, rgba(9,18,26,.15) 100%);
}
.t26-banner__content {
  position: relative; z-index: 2;
  padding: clamp(22px, 4vw, 44px);
  color: white;
  max-width: 640px;
}
.t26-banner__eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-size: 11px; font-weight: 700;
  letter-spacing: .18em; text-transform: uppercase;
  color: rgba(255,255,255,.92);
  background: rgba(255,255,255,.14);
  border: 1px solid rgba(255,255,255,.22);
  padding: 6px 12px;
  border-radius: var(--r-pill);
  margin-bottom: 14px;
  backdrop-filter: blur(6px);
}
.t26-banner__title {
  font-family: var(--font-display);
  margin: 0 0 6px;
  font-size: clamp(22px, 3.2vw, 34px);
  font-weight: 700;
  letter-spacing: -.01em;
  line-height: 1.1;
  text-wrap: balance;
  color: white;
}
.t26-banner__subtitle {
  margin: 0 0 10px;
  color: rgba(255,255,255,.92);
  font-size: 17px;
  font-weight: 500;
}
.t26-banner__desc {
  margin: 0 0 18px;
  color: rgba(255,255,255,.82);
  font-size: 15px; line-height: 1.5;
  max-width: 520px;
  text-wrap: pretty;
}
.t26-banner__ctas { display: inline-flex; flex-wrap: wrap; gap: 10px; }
/* Force la lisibilité des boutons sur image sombre, quelle que soit la
   couleur primary/secondary configurée par l'admin */
.t26-banner__ctas .t26-btn--primary,
.t26-banner__ctas .t26-btn--secondary {
  color: #fff;
}
`;

function BannerCta({ org, label, url, icon, variant }) {
    if (!label) return null;
    const iconNode = icon ? renderIcon(icon, 18) : null;
    if (!url) return <Button variant={variant} size="md" as="button">{iconNode}{label}</Button>;
    if (isExternalUrl(url, org)) {
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <Button variant={variant} size="md" as="span">{iconNode}{label}</Button>
            </a>
        );
    }
    return (
        <Link href={buildLinkHref(resolveUrl(org, url), org)}>
            <Button variant={variant} size="md" as="span">{iconNode}{label}</Button>
        </Link>
    );
}

/**
 * Section Banners : empile en stack vertical les bannières remixées.
 * Structure de chaque item identique à un slide hero (image + eyebrow +
 * title + subtitle + description + btn1/btn2 avec icons). Multilangue via applyLang.
 *
 * Source paramétrable :
 *   <Banners source="banners.items" />        ← défaut
 *   <Banners source="banners.someOther" />    ← réutilisable pour des contextes spécifiques
 */
export default function Banners({ context, source = "banners.items" }) {
    const { org, config, lang } = context;
    const items = readArrayFromConfig(config, source, []).filter(b => b?.image);
    if (items.length === 0) return null;

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: styles }} />
            <div className="t26-banners-wrap">
                {items.map((banner, i) => {
                    const localized = applyLang(banner, lang);
                    return (
                        <section key={banner.id || i} className="t26-banner">
                            <img src={resolveImage(localized.image)} alt="" loading="lazy" className="t26-banner__img" />
                            <div className="t26-banner__wash" />
                            <div className="t26-banner__content">
                                {localized.eyebrow && (
                                    <span className="t26-banner__eyebrow">
                                        {banner.eyebrow_icon && renderIcon(banner.eyebrow_icon, 14)}
                                        {localized.eyebrow}
                                    </span>
                                )}
                                {localized.title && <h3 className="t26-banner__title">{localized.title}</h3>}
                                {localized.subtitle && <p className="t26-banner__subtitle">{localized.subtitle}</p>}
                                {localized.description && <p className="t26-banner__desc">{localized.description}</p>}
                                {(localized.btn1_label || localized.btn2_label) && (
                                    <div className="t26-banner__ctas">
                                        <BannerCta org={org} label={localized.btn1_label} url={localized.btn1_url} icon={banner.btn1_icon} variant="secondary" />
                                        <BannerCta org={org} label={localized.btn2_label} url={localized.btn2_url} icon={banner.btn2_icon} variant="ghost" />
                                    </div>
                                )}
                            </div>
                        </section>
                    );
                })}
            </div>
        </>
    );
}
